/*
Toggle class on another element.

HTML Usage:
Using Single toggleClass and target:
<toggle-element-class data-options='{
  "toggleClass": "toggle--is-active",
  "target": ".tab-washing-content",
  "closeOthers": true,
  "otherClassesTargets": [".tab-content", ".another-class"]
}'>
  Toggle element class button
</toggle-element-class>

Using Multiple toggleClasses:
<toggle-element-class data-options='{
  "toggleClasses": [
    {"class": "toggle--is-active", "target": ".tab-washing-content"},
    {"class": "testClass", "target": ".tab-details-content"}
  ],
  "closeOthers": true,
  "otherClassesTargets": [".tab-content", ".another-class"]
}'>
  Toggle element class button
</toggle-element-class>

*/

class ToggleElementClass extends HTMLElement {
  constructor() {
    super();

    // Options
    this.options = {
      toggleClass: 'toggle--is-active',
      target: 'body',
      toggleClasses: [], // Array to hold multiple class/target pairs
      toggleTriggerClass: '',
      onHover: false,
      hoverDelay: 0,
      preventDefault: true,
      stopPropagation: true,
      ariaExpanded: false,
      closeOthers: false,
      otherClassesTargets: [], // Array to hold multiple selectors
    };

    // Get options from element data and combine with this.options
    if (this?.dataset?.options) {
      const dataOptions = JSON.parse(this.dataset.options);
      this.options = {
        ...this.options,
        ...dataOptions,
      };
    }

    // Ensure backward compatibility
    if (this.options.toggleClass && this.options.target) {
      this.options.toggleClasses.push({
        class: this.options.toggleClass,
        target: this.options.target,
      });
    }

    // Reduce actions (So the event can also be removed)
    this.reducer = {
      click: (e) => this.debounceClickEvent(e),
      mouseEnter: (e) => this.debouncedOnMouse(e, 'add'),
      mouseLeave: (e) => this.debouncedOnMouse(e, 'remove'),
    };

    // Construct webcomponent
    this.construct();
  }

  /*
      Construct webcomponent
    */
  construct() {
    this.targets = this.options.toggleClasses.map((tc) => ({
      element: document.querySelector(tc.target),
      class: tc.class,
    }));

    if (this.targets.some((t) => !t.element)) return false;

    // Click
    this.removeEventListener('click', this.reducer.click);
    this.addEventListener('click', this.reducer.click);

    // Mouse enter and leave
    if (this.options.onHover) {
      this.removeEventListener('mouseenter', this.reducer.mouseEnter);
      this.addEventListener('mouseleave', this.reducer.mouseLeave);
    }
  }

  /*
      Debounce click event
      @param e {object}: mouseevent
      Called by add event listener -> reducer.
    */
  debounceClickEvent(e) {
    if (!e) return false;
    this.preventDefault(e);
    this.stopPropagation(e);
    this.toggle();
  }

  /*
      Debounced mouse event
      @param e {object}: mouseevent
      @param type {string}: open/close/closeAll
      Called by add event listener -> reducer.
    */
  debouncedOnMouse(e, type) {
    if (!e) return false;

    const debouncer = debounce((type) => {
      switch (type) {
        case 'add':
          this.add();
          break;
        case 'remove':
          this.remove();
          break;
        default:
          this.toggle();
          break;
      }
    }, this.options.hoverDelay);
    debouncer(type);
  }

  // @param e {object}: click event
  preventDefault(e) {
    if (this.options.preventDefault) e.preventDefault();
  }

  // @param e {object}: click event
  stopPropagation(e) {
    if (this.options.stopPropagation) e.stopPropagation();
  }

  add() {
    const { toggleTriggerClass } = this.options;
    this.targets.forEach(({ element, class: toggleClass }) => element?.classList.add(toggleClass));
    if (toggleTriggerClass) this.classList.add(toggleTriggerClass);
    if (this.options.ariaExpanded) this.setAttribute('aria-expanded', true);
  }

  remove() {
    const { toggleTriggerClass } = this.options;
    this.targets.forEach(({ element, class: toggleClass }) => {
      element?.classList.remove(toggleClass);
    });
    if (toggleTriggerClass) this.classList.remove(toggleTriggerClass);
    if (this.options.ariaExpanded) this.setAttribute('aria-expanded', false);
  }

  toggle() {
    const { closeOthers, otherClassesTargets } = this.options;
    if (closeOthers && otherClassesTargets.length > 0) this.closeOtherElements(otherClassesTargets);

    if (
      this.targets.some(
        ({ element, class: toggleClass }) => !element.classList.contains(toggleClass)
      )
    ) {
      this.add();
    } else {
      this.remove();
    }
  }

  closeOtherElements(otherClassesTargets) {
    otherClassesTargets.forEach((selector) => {
      const elements = document.querySelectorAll(selector);
      elements.forEach((element) => {
        this.targets.forEach(({ class: toggleClass }) => {
          if (element !== this.target) element.classList.remove(toggleClass);
        });
      });
    });
  }
}

customElements.define('toggle-element-class', ToggleElementClass);
